import { FunctionComponent, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Datepicker, { DateValueType } from "react-tailwindcss-datepicker";

import { Dropdown } from "@/components/Dropdown/Dropdown";

import { LAST_CONTACT_OPTIONS, LastContactPreset } from "../constants/reengage.constants";

interface LastContactRange {
	from: string;
	till: string;
}

interface LastContactSelectProps {
	value: LastContactPreset;
	onChange: (preset: LastContactPreset) => void;
	range: LastContactRange | null;
	onRangeChange: (range: LastContactRange | null) => void;
	error?: boolean;
}

const toLocalDate = (date: Date | string | null | undefined): string => {
	if (!date) {
		return "";
	}

	const parsed = typeof date === "string" ? new Date(date) : date;
	const year = parsed.getFullYear();
	const month = String(parsed.getMonth() + 1).padStart(2, "0");
	const day = String(parsed.getDate()).padStart(2, "0");

	return `${year}-${month}-${day}`;
};

export const LastContactSelect: FunctionComponent<LastContactSelectProps> = ({
	value,
	onChange,
	range,
	onRangeChange,
	error,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`list.reengage.${key}`);

	const options = useMemo(
		() =>
			LAST_CONTACT_OPTIONS.map((option) => ({
				id: option.id,
				title: option.id === "custom" ? ts("lastContactCustom") : ts(`lastContact_${option.id}`),
			})),
		[t]
	);

	const selected = useMemo(() => options.find((o) => o.id === value), [options, value]);

	const dateValue: DateValueType = useMemo(
		() => ({
			startDate: range?.from ? new Date(range.from) : null,
			endDate: range?.till ? new Date(range.till) : null,
		}),
		[range]
	);

	const handleRangeChange = (newValue: DateValueType) => {
		if (newValue?.startDate && newValue?.endDate) {
			onRangeChange({ from: toLocalDate(newValue.startDate), till: toLocalDate(newValue.endDate) });
		} else {
			onRangeChange(null);
		}
	};

	return (
		<div>
			<Dropdown
				floating
				portal
				data={options}
				defaultValue={selected}
				handleSelect={(item) => onChange(item.id as LastContactPreset)}
			/>
			{value === "custom" && (
				<div className="mt-3 p-3 bg-background-light rounded-lg border border-dashed border-border">
					<Datepicker
						useRange
						containerClassName="border border-border-light rounded-xl p-0 text-sm text-text-meta relative"
						inputClassName="relative py-2.5 pl-4 pr-14 w-full border-gray-300 rounded-xl text-sm placeholder-gray-400 bg-white focus:border-blue-500 focus:ring focus:ring-blue-500/20"
						maxDate={new Date()}
						placeholder={ts("lastContactRangePlaceholder")}
						popoverDirection="down"
						primaryColor="blue"
						separator="-"
						value={dateValue}
						onChange={handleRangeChange}
					/>
					{error && <p className="mt-2 text-xs text-status-error">{ts("requiredField")}</p>}
				</div>
			)}
		</div>
	);
};
